import React, { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';

// Import types
import { MockupData, ChatMessage, ModalInfo } from '../types';
import { getAIAnalysis, ReportViewModel } from '@hone-automation/common';
import { AIQueryTypes, AIProviders } from '@hone-automation/common';

// Import mockup data for initial development (fallback only)
import mockupData from '../data/mockup.json';

// Import AI Analysis service
import {
  generateSummaryPromptFromKSExportData,
  generateSummaryPromptFromAIAnalysisData,
} from '../services/AIAnalysisService';

// Import AI query hook and parser
import { useAIQuery } from 'presentation/components/KAI/KAI.hook';
import { AIResponseObject, parseAIResponse } from 'presentation/utils/aiJSONParser';

// Import cache utilities
import { generateCacheKey, setItemWithEviction, getItem } from 'utils/cacheUtils';

// Define the shape of our context
export interface AIAnalysisContextType {
  summaryData: MockupData | null;
  isSummaryLoading: boolean;
  summaryError: Error | null;
  summaryWarning: string | null;
  showReport: boolean;
  setShowReport: (show: boolean) => void;
  chatHistory: ChatMessage[];
  isChatLoading: boolean;
  chatError: Error | null;
  modalInfo: ModalInfo | null;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  refreshSummary: () => Promise<void>;
  loadSummaryFromCache: (forceRefresh?: boolean) => Promise<void>;
  sendChatMessage: (message: string) => Promise<void>;
  showInfoModal: (content: ReactNode, title?: string) => void;
  hideInfoModal: () => void;
  scrollToTop: () => void;
  scrollToBottom: () => void;
  username?: string;
  aiAnalysis: any | null; // Raw AI analysis data from the report
  cacheDate?: string; // Date when the cache was created
  reversed?: boolean;
  isUsingLLMResult?: boolean;
}

// Create the context with a default value
const AIAnalysisContext = createContext<AIAnalysisContextType | undefined>(undefined);

// Cache constants
const CACHE_PREFIX = 'ai-analysis-summary';
const CACHE_EXPIRY_TIME = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds

// Function to generate cache keys based on report parameters and URL parameters
const generateCacheKeys = (reportData: ReportViewModel | null | undefined) => {
  if (!reportData) return { dataKey: '' };

  // Extract relevant parameters that affect the report content
  const { reportId } = reportData;

  // Get URL parameters that affect the report content
  const urlParams = new URLSearchParams(window.location.search);

  // Extract the parameters that affect the report content
  const relevantParams = [
    'type',
    'difference',
    'budget',
    'customDateRange',
    'breakdownPeriods',
    'timeframe',
    'comparisonType',
    'comparisonPeriods',
    'compareEnabled',
    'dateRange',
    'total',
  ];

  // Build a string of relevant URL parameters
  const urlParamsString = relevantParams.map(param => `${param}=${urlParams.get(param) || ''}`).join('-');

  // Create a unique identifier from report parameters and URL parameters
  const uniqueId = `${reportId || ''}-${urlParamsString}`;

  // Use hash-based keys to reduce key length and avoid quota issues
  const dataKey = generateCacheKey(`${CACHE_PREFIX}-data`, uniqueId);

  return {
    dataKey,
  };
};

interface AIAnalysisProviderProps {
  children: ReactNode;
  reportData?: ReportViewModel | null;
  selectedRowId?: string | null;
  selectedColumnId?: string | number | null;
  username?: string;
  extendedContext?: { location?: string; user?: string; userName?: string };
  reversed?: boolean;
  LLMResult?: string;
}

export const AIAnalysisProvider: React.FC<AIAnalysisProviderProps> = ({
  children,
  reportData,
  username,
  extendedContext,
  reversed,
  LLMResult,
}) => {
  // AI query hook
  const { sendQuery, loading: aiQueryLoading } = useAIQuery();

  // Summary states
  const [summaryData, setSummaryData] = useState<MockupData | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(true);
  const [summaryError, setSummaryError] = useState<Error | null>(null);
  const [summaryWarning, setSummaryWarning] = useState<string | null>(null);

  // UI view states
  const [showReport, setShowReport] = useState<boolean>(false);

  // Chat states
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const [chatError, setChatError] = useState<Error | null>(null);

  // UI states
  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);

  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Function to refresh the summary data
  const refreshSummary = async () => {
    const aiAnalysisData = getAIAnalysis(reportData);
    if (!reportData || !aiAnalysisData) {
      setSummaryWarning('No report data available');
      return;
    }

    // Get cache key for this specific report
    const { dataKey } = generateCacheKeys(reportData);

    try {
      setIsSummaryLoading(true);
      setSummaryError(null);
      setSummaryWarning(null);

      // Generate prompt from the AI analysis data
      // Get the cache date if available (prefer embedded timestamp; fallback to legacy separate key)
      let cacheDate: string | undefined = undefined;
      try {
        const existing = dataKey ? getItem(dataKey) : null;
        if (existing) {
          const parsed = JSON.parse(existing);
          const embeddedTs =
            typeof parsed === 'object' && parsed && typeof parsed.timestamp === 'number' ? parsed.timestamp : null;
          if (embeddedTs) cacheDate = new Date(embeddedTs).toISOString();
        }
      } catch {
        // Ignore JSON parse errors
      }
      // No separate timestamp key fallback; we only use embedded timestamps now

      // Pass the aiAnalysis data and cache date to the prompt generation function
      const prompt = await generateSummaryPromptFromAIAnalysisData(
        aiAnalysisData,
        username || 'User',
        extendedContext,
        cacheDate
      );
      // Send the query to the AI service
      const response = await sendQuery(prompt, AIQueryTypes.Report_Analysis, AIProviders.OpenAI, 'gpt-4.1-mini');

      if (response.content) {
        // Create a properly typed response object for the parser
        const aiResponse = {
          content: response.content,
          model: response.model,
          provider: response.provider,
          timestamp: new Date().toISOString(),
          ...response, // Include any other properties
        };

        // Use the AI JSON Parser to handle the response
        const parseResult = parseAIResponse(aiResponse, { debug: true });

        if ('error' in parseResult) {
          // Handle parsing error
          const errorMsg = `Failed to parse AI response: ${parseResult.message}`;
          setSummaryError(new Error(errorMsg));
          console.error('AI Response Parsing Error:', parseResult);
          console.error('Original LLM Response:', response.content);

          // Log the response in a more readable format
          try {
            /*             console.log('Response Content Type:', typeof response.content);
            console.log('Response Content Length:', response.content?.length || 0);
            console.log('Response Content Preview:', response.content?.substring(0, 500) + '...'); */
          } catch (logError) {
            console.error('Error logging response details:', logError);
          }
        } else {
          // Successfully parsed the response
          setSummaryData(parseResult as MockupData);

          // Cache the data in localStorage with report-specific keys
          // Use setItemWithEviction to automatically handle quota exceeded errors
          if (dataKey) {
            const payload = { timestamp: Date.now(), data: parseResult };
            const dataStored = setItemWithEviction(
              dataKey,
              JSON.stringify(payload),
              `${CACHE_PREFIX}-data`,
              10 // Max 10 eviction attempts
            );

            if (!dataStored) {
              console.warn('[AIAnalysis] Failed to cache analysis data after eviction attempts');
            }
          }
        }
      } else {
        setSummaryError(new Error('No response from AI'));
        console.error('No response from AI', response);
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
      setSummaryError(error instanceof Error ? error : new Error('Unknown error occurred'));
    } finally {
      setIsSummaryLoading(false);
    }
  };

  // Function to load summary data from cache or fetch new data
  const loadSummaryFromCache = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        await refreshSummary();
        return;
      }

      const { dataKey } = generateCacheKeys(reportData);

      // If we don't have valid cache key, fetch new data
      if (!dataKey) {
        await refreshSummary();
        return;
      }

      const cachedData = dataKey ? getItem(dataKey) : null;
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          // Support new format { timestamp, data } and legacy format (raw data with separate timestamp key)
          const embeddedTs =
            typeof parsed === 'object' && parsed && typeof parsed.timestamp === 'number' ? parsed.timestamp : null;
          const dataPayload = typeof parsed === 'object' && parsed && parsed.data ? parsed.data : parsed;

          const timestamp = embeddedTs;

          if (timestamp) {
            const now = Date.now();
            if (now - timestamp < CACHE_EXPIRY_TIME) {
              setSummaryData(dataPayload as MockupData);
              setIsSummaryLoading(false);
              return;
            }
          }
        } catch (e) {
          // fallback to refresh
        }
      }

      // If no valid cache, fetch new data
      await refreshSummary();
    } catch (error) {
      console.error('Error loading cached summary:', error);
      await refreshSummary();
    }
  };

  // Load data from report or cache when reportData changes
  useEffect(() => {
    // Cancellation flag to prevent state updates after unmount
    let cancelled = false;

    // Async function to load data
    const loadData = async () => {
      if (cancelled) return;

      if (LLMResult) {
        // If we have a direct LLM result, parse it and set as summary data
        try {
          // Create a proper AIResponseObject from the string
          const responseObject: AIResponseObject = { content: LLMResult };
          const parsedData = parseAIResponse(responseObject);

          if (cancelled) return;

          // Check if the parsed data has the expected structure
          if (parsedData && typeof parsedData === 'object' && !('error' in parsedData)) {
            // Convert to MockupData format if needed
            const mockupData: MockupData = {
              greeting: {
                salutation: 'Hello',
                message: 'there has been an error requesting the analysis.',
                callToAction: 'You can inspect the error by switching to data view.',
                reportAvailable: true,
                reportPreview: '',
              },
              report: {
                header: {
                  title: 'AI Analysis',
                  subtitle: 'Generated from prompt',
                  status: {
                    label: 'Error',
                    severity: 'negative',
                  },
                },
                conceptBlocks: [],
                chatSuggestions: [],
              },
            };

            // If the parsed data has a structure that matches our expected format, use it
            if ('greeting' in parsedData && 'report' in parsedData) {
              setSummaryData(parsedData as MockupData);
            } else {
              // Otherwise, try to adapt the data to our format
              if ('conceptBlocks' in parsedData) {
                mockupData.report.conceptBlocks = parsedData.conceptBlocks;
              }
              setSummaryData(mockupData);
            }

            setIsSummaryLoading(false);
            setSummaryError(null);
            setSummaryWarning(null);
          } else {
            throw new Error('Parsed data does not have the expected structure');
          }
        } catch (error) {
          if (cancelled) return;
          console.error('Error parsing LLM result:', error);
          setSummaryError(error instanceof Error ? error : new Error('Failed to parse LLM result'));
          setSummaryWarning('The LLM response could not be parsed correctly. Please check the format.');
          setIsSummaryLoading(false);
        }
      } else if (reportData && getAIAnalysis(reportData)) {
        // Try to load from cache first, only refresh if cache is invalid
        await loadSummaryFromCache(false);
      } else {
        // Fall back to cached data if no report data is available
        await loadSummaryFromCache(false);
      }
    };

    // Execute the async load function
    loadData();

    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [reportData?.reportId, reportData?.analysis?.aiInsights, LLMResult]); // Use primitive dependencies instead of object (V2 schema)

  // Function to send a chat message
  const sendChatMessage = async (message: string) => {
    try {
      // Create a properly formatted user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        content: message,
        sender: 'user',
        timestamp: new Date(),
      };

      setChatHistory(prev => [...prev, userMessage]);
      setIsChatLoading(true);
      setChatError(null);

      // TODO: Implement real AI chat functionality
      // For now, just simulate a response after a delay
      setTimeout(() => {
        try {
          // Create a properly formatted AI response message
          const aiMessage: ChatMessage = {
            id: `ai-${Date.now()}`,
            content: `This is a placeholder response to: "${message}"`,
            sender: 'ai',
            timestamp: new Date(),
          };

          setChatHistory(prev => [...prev, aiMessage]);
          setChatError(null);
        } catch (error) {
          setChatError(error instanceof Error ? error : new Error('Unknown error occurred'));
        } finally {
          setIsChatLoading(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Error sending chat message:', error);
    }
  };

  // Modal functions
  const showInfoModal = (content: ReactNode, title?: string) => {
    setModalInfo({ content, title });
  };

  const hideInfoModal = () => {
    setModalInfo(null);
  };

  // Scroll functions
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      const element = scrollContainerRef.current;
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const closeModule = () => {
    if (closeModule) {
      closeModule();
    }
  };

  // Get the cache date if available
  const { dataKey } = generateCacheKeys(reportData);
  let cacheDate: string | undefined = undefined;
  try {
    const existing = dataKey ? getItem(dataKey) : null;
    if (existing) {
      const parsed = JSON.parse(existing);
      const embeddedTs =
        typeof parsed === 'object' && parsed && typeof parsed.timestamp === 'number' ? parsed.timestamp : null;
      if (embeddedTs) cacheDate = new Date(embeddedTs).toISOString();
    }
  } catch {
    // Ignore JSON parse errors
  }
  // No separate timestamp key fallback; we only use embedded timestamps now

  // Provide the context value
  const contextValue: AIAnalysisContextType = {
    summaryData,
    isSummaryLoading,
    summaryError,
    summaryWarning,
    showReport,
    setShowReport,
    chatHistory,
    isChatLoading,
    chatError,
    modalInfo,
    scrollContainerRef,
    refreshSummary,
    loadSummaryFromCache,
    sendChatMessage,
    showInfoModal,
    hideInfoModal,
    scrollToTop,
    scrollToBottom,
    username,
    aiAnalysis: getAIAnalysis(reportData) || null, // Add the raw AI analysis data (v1 and v2 compatible)
    cacheDate, // Add the cache date
    reversed,
    isUsingLLMResult: !!LLMResult,
  };

  return <AIAnalysisContext.Provider value={contextValue}>{children}</AIAnalysisContext.Provider>;
};

// Custom hook to use the context
export const useAIAnalysis = (username?: string) => {
  const context = useContext(AIAnalysisContext);
  if (context === undefined) {
    throw new Error('useAIAnalysis must be used within an AIAnalysisProvider');
  }
  return context;
};
